import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import find from 'lodash/find';
import XLSX from 'xlsx/dist/xlsx.core.min';
import css from './index.scss';
import messages from './messages';
import ItemSelector from './item-selector';
import Button from '../button/index';
import { DARK } from '../button/variants';

class CourseDetails extends Component {
    constructor(props) {
        super(props);

        this.state = {
            course: {},
            activeTopicId: null
        };

        this.setActiveCourse = this.setActiveCourse.bind(this);
        this.exportCourse = this.exportCourse.bind(this);
    }

    setActiveCourse(id) {
        this.setState({ activeTopicId: 0 });
        setTimeout(() => {
            this.setState({ activeTopicId: id });
        }, 5);
    }

    getActiveTopicId() {
        const { course } = this.props;
        const { topics } = course;
        let selectedTopicId = this.state.activeTopicId;

        // set initial topic index on first load
        if (this.state.activeTopicId === null && topics.length > 0) {
            selectedTopicId = topics[0].id;
        }

        return selectedTopicId;
    }

    getTopics() {
        const { course } = this.props;
        const { topics } = course;
        const activeTopicId = this.getActiveTopicId();

        const list = topics.map((item) => {
            const isSelected = item.id === activeTopicId;
            return (<ItemSelector
                handleOnClick={() => this.setActiveCourse(item.id)}
                key={item.id}
                label={item.name}
                selected={isSelected} />);
        });

        return list;
    }

    getActiveActivities() {
        const { course, intl } = this.props;
        const { topics } = course;
        const activeTopicId = this.getActiveTopicId();

        // Get active or selected course
        const activeTopic = find(topics, item => item.id === activeTopicId);

        // Get active or selected course
        let subList = [];

        if (activeTopic) {
            const { core, easier } = activeTopic;
            const { activities: coreActivities } = core;
            const { activities: easierActivities } = easier;
            const flag = true;

            subList = coreActivities.map(item => (<ItemSelector
                guideUrl={item.guideUrl}
                hasGuide={flag}
                hasNoCursor={flag}
                key={item.id}
                label={item.name} />));

            if (easierActivities.length > 0) {
                subList.unshift(<ItemSelector
                    hasNoCursor={flag}
                    key="easier"
                    label={intl.formatMessage(messages.areYouReadyLabel)} />);
            }

            if (coreActivities.length > 0) {
                subList.push(<ItemSelector
                    hasNoCursor={flag}
                    key="core"
                    label={intl.formatMessage(messages.testLabel)} />);
            }
        }
        return subList;
    }

    exportCourse() {
        const { course, intl } = this.props;
        const { author, country, topics, name, yearGroups } = course;

        const countryLabel = intl.formatMessage(messages.countryLabel);
        const gradeLabel = intl.formatMessage(messages.gradeLabel);
        const authorLabel = intl.formatMessage(messages.authorLabel);

        const mergedYearGroups = Array(yearGroups.map(a => a.name)).join(', ');

        let exportData = [];
        topics.forEach((topic) => {
            const topicName = topic.name;
            const { core, easier } = topic;
            const { activities: coreActivities } = core;
            const { activities: easierActivities } = easier;
            let topicActivityList = [];

            topicActivityList = coreActivities.map(item => ([`  ${topicName}`, `  ${item.name}`]));

            if (easierActivities.length > 0) {
                topicActivityList.unshift([`  ${topicName}`, `  ${intl.formatMessage(messages.areYouReadyLabel)}`]);
            }

            if (coreActivities.length > 0) {
                topicActivityList.push([`  ${topicName}`, `  ${intl.formatMessage(messages.testLabel)}`]);
            }

            easierActivities.forEach((activity, index) => {
                topicActivityList[index].splice(2, 0, activity.name);
            });

            exportData = [...exportData, ...topicActivityList];
        });

        /* merge cells A1:C1 */
        const titleMerge = { s: { r: 0, c: 0 }, e: { r: 0, c: 10 } };

        /* merge cells A2:C2 */
        const subTitleMerge = { s: { r: 1, c: 0 }, e: { r: 1, c: 10 } };

        const heading = [
            [name],
            [`${countryLabel}: ${country} | ${gradeLabel}: ${mergedYearGroups} | ${authorLabel}: ${author}`],
            [],
            [intl.formatMessage(messages.topicFileLabel),
                intl.formatMessage(messages.activitiesFileLabel),
                intl.formatMessage(messages.easierActivitiesFileLabel)]
        ];

        // Add headers
        const worksheet = XLSX.utils.aoa_to_sheet(heading);

        /* add merges */
        if (!worksheet['!merges']) worksheet['!merges'] = [];
        worksheet['!merges'].push(titleMerge);
        worksheet['!merges'].push(subTitleMerge);

        // Append data
        XLSX.utils.sheet_add_aoa(worksheet, exportData, { origin: 4, skipHeader: true });
        // Create a blank workbook using SheetJS
        const workbook = XLSX.utils.book_new();
        // Append the JSON sheet
        XLSX.utils.book_append_sheet(workbook, worksheet);

        XLSX.writeFile(workbook, `${name}.xlsx`);
    }

    render() {
        const { course, intl, css: cssprops } = this.props;
        const { name } = course;

        // Combine css and propped css
        const styles = { ...css, ...cssprops };

        // Get all topics
        const list = this.getTopics();

        // Filter activites for the selected topic
        const activities = this.getActiveActivities();

        return (<div className={styles.container}>
            <div className={`${styles.header} ${styles.headerColor}`}>
                <h2>{name}</h2>
                <Button handleOnClick={this.exportCourse} variant={DARK}>{intl.formatMessage(messages.exportLabel)}</Button>
            </div>
            <div className={`${styles.list} ${styles.height}`}>
                <div className={styles.leftPanel}>
                    <div>
                        <div className={styles.panelLabel}>{intl.formatMessage(messages.topicLabel)}</div>
                        {list}
                    </div>
                </div>
                <div className={styles.rightPanel}>
                    <div>
                        <div className={styles.panelLabel}>{intl.formatMessage(messages.activitiesLabel)}</div>
                        {activities}
                    </div>
                </div>
            </div>
        </div>);
    }
}

CourseDetails.propTypes = {
    /**
     * Refer to CourseManagerGateway for Course Object
     */
    course: PropTypes.object,
    /**
     * Hashed CSS or stylesheets
     */
    css: PropTypes.object,
    intl: intlShape.isRequired
};

CourseDetails.defaultProps = {
    course: {
        name: '',
        courses: []
    }
};

export default injectIntl(CourseDetails);



// WEBPACK FOOTER //
// ./app/components/course-topics-details/index.js